// Postęp porady szybkiej w tle (karta PORADA-TLE, 27.10.2026) — WSPÓLNY dla klasyka (`pages/AdvicePage.jsx`) i cockpitu
// (`ui-cockpit/views/AdviceView.jsx`). Obie strony są zamrożone (macierz zamrożeń, grupa A), więc nie mogą same pokazać
// etapu ani świeżości postępu: `lib/api.js` (quickAdvice / quickAdviceWithFile) zapisuje tu stan zadania, a globalny
// wskaźnik `components/PostepPoradySzybkiej.jsx` (montowany w App.js poza AppShell, bo cockpit nie ma AppShell) go rysuje.
//
// Zasada Miro: „żaden panel nie twierdzi niczego, czego nie policzył”. Dlatego trzymamy TRZY osobne chwile:
//   * `start` — kiedy serwer przyjął zadanie (202), z tego liczymy czas od startu;
//   * `ostatniaAktualizacja` — kiedy backend ostatnio ZMIENIŁ etap (`postep.stage/label/at` inne niż poprzednio);
//     po `PROG_BRAKU_AKTUALIZACJI_MS` bez zmiany wskaźnik mówi „brak aktualizacji od N s”, nie „trwa”;
//   * `ostatnieSprawdzenie` — kiedy ostatnio udało się odczytać stan zadania (serwer odpowiada na GET statusu).
// PORADA-TLE-2 (31.10.2026): CZAS OD STARTU liczymy zegarem SERWERA, gdy go znamy (`started_at`/`finished_at`/`czas_serwera`
// z odczytu statusu, patrz `lib/czasZadania.js`) — zegar przeglądarki od 202 do „zauważyłem koniec” dał 30.10 licznik
// „111 minut” dla zadania, które trwało 20,5 min. Bez znaczników serwera (stary backend) — jak dotąd, zegar przeglądarki.
// Czysty JS, bez Reacta i bez storage (treść porady nigdy tu nie trafia — tylko numer etapu i jego etykieta).
import { znacznikiSerwera, czasZadaniaMs } from "./czasZadania";

export const PROG_BRAKU_AKTUALIZACJI_MS = 60 * 1000;
/** Ile wskaźnik pokazuje stan końcowy (gotowe / błąd), zanim zniknie. */
export const CZAS_POKAZU_KONCA_MS = 4000;

let stan = null;
const sluchacze = new Set();

function powiadom() {
  sluchacze.forEach((fn) => {
    try { fn(); } catch { /* słuchacz nie może zepsuć sklepu */ }
  });
}

/** Subskrypcja zmian; zwraca funkcję odpinającą. Zgodna z `useSyncExternalStore`. */
export function subskrybuj(fn) {
  sluchacze.add(fn);
  return () => sluchacze.delete(fn);
}

/** Migawka stanu (ta sama referencja, dopóki nic się nie zmieniło — wymóg `useSyncExternalStore`) albo `null`. */
export function stanPostepu() {
  return stan;
}

/** `{stage, total, label, pct, at}` z backendu → `{numer, razem, etykieta, procent, at}`; pola nieznane = null. */
export function normalizujPostep(p) {
  if (!p || typeof p !== "object") return null;
  const liczba = (v) => (v !== null && v !== undefined && v !== "" && Number.isFinite(Number(v)) ? Number(v) : null);
  const tekst = (v) => (typeof v === "string" && v.trim() ? v.trim() : null);
  const etap = { numer: liczba(p.stage), razem: liczba(p.total), etykieta: tekst(p.label), procent: liczba(p.pct), at: tekst(p.at) };
  if (etap.procent === null && etap.numer !== null && etap.razem) etap.procent = Math.round((etap.numer / etap.razem) * 100);
  return etap.numer === null && etap.razem === null && etap.etykieta === null ? null : etap;
}

const tenSamEtap = (a, b) => Boolean(a && b) && a.numer === b.numer && a.etykieta === b.etykieta && a.at === b.at;

/** Serwer przyjął zadanie porady (202 z `job_id`; `rodzaj`: "quick" | "deep"). Zastępuje poprzedni stan (także końcowy). */
export function rozpocznij({ rodzaj = "quick", jobId = null, teraz = Date.now() } = {}) {
  stan = {
    rodzaj, jobId, start: teraz, etap: null, ostatniaAktualizacja: null, ostatnieSprawdzenie: null, status: "trwa", koniec: null,
    serwer: { startMs: null, koniecMs: null, przesuniecieMs: null },
  };
  powiadom();
  return stan;
}

/** Udany odczyt stanu zadania (GET statusu odpowiedział) — niezależnie od tego, czy etap się zmienił. */
export function odnotujSprawdzenie(teraz = Date.now()) {
  if (!stan || stan.status !== "trwa") return;
  stan = { ...stan, ostatnieSprawdzenie: teraz };
  powiadom();
}

/**
 * Znaczniki czasu serwera z odpowiedzi statusu (`started_at`, `finished_at`, `czas_serwera`) — wołane po KAŻDYM udanym
 * odczycie, także po ostatnim (`done`/`error`), zanim sklep dostanie `zakoncz`. Odpowiedź bez tych pól nic nie zmienia.
 */
export function odnotujStatus(d, teraz = Date.now()) {
  if (!stan || stan.status !== "trwa") return;
  const zn = znacznikiSerwera(d, teraz);
  const stare = stan.serwer;
  const startMs = zn.startMs ?? stare.startMs;
  const koniecMs = zn.koniecMs ?? stare.koniecMs;
  const przesuniecieMs = zn.przesuniecieMs ?? stare.przesuniecieMs;
  if (startMs === stare.startMs && koniecMs === stare.koniecMs && przesuniecieMs === stare.przesuniecieMs) return;
  stan = { ...stan, serwer: { startMs, koniecMs, przesuniecieMs } };
  powiadom();
}

/** Postęp z odpowiedzi statusu; `ostatniaAktualizacja` rusza TYLKO, gdy backend zmienił etap. */
export function odnotujPostep(p, teraz = Date.now()) {
  if (!stan || stan.status !== "trwa") return;
  const etap = normalizujPostep(p);
  if (!etap || tenSamEtap(stan.etap, etap)) return;
  stan = { ...stan, etap, ostatniaAktualizacja: teraz };
  powiadom();
}

/** Koniec zadania: `gotowe` albo `blad`. Bez trwającego zadania — nic (tryb klasyczny nie ma wskaźnika). */
export function zakoncz(status, teraz = Date.now()) {
  if (!stan || stan.status !== "trwa") return;
  stan = { ...stan, status: status === "gotowe" ? "gotowe" : "blad", koniec: teraz };
  powiadom();
}

export function wyczysc() {
  if (stan === null) return;
  stan = null;
  powiadom();
}

/** `m:ss` albo `g:mm:ss`; wartość nieliczbowa → `—`. */
export function formatCzas(ms) {
  if (!Number.isFinite(ms) || ms < 0) return "—";
  const s = Math.floor(ms / 1000);
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const ss = String(s % 60).padStart(2, "0");
  return h > 0 ? `${h}:${String(m).padStart(2, "0")}:${ss}` : `${m}:${ss}`;
}

/** Czas zadania wg zegara serwera (patrz `lib/czasZadania.js`) albo null, gdy serwer nie podał `started_at`. Zadanie, które
 *  przeglądarka uznała za zakończone bez `finished_at` (np. błąd sieci), kończy się w chwili `koniec` przeliczonej na zegar serwera. */
function czasSerwera(s, teraz) {
  const zn = s.serwer;
  if (!zn || zn.startMs === null) return null;
  const przesuniecie = zn.przesuniecieMs ?? 0;
  const koniecMs = zn.koniecMs ?? (s.koniec !== null && s.koniec !== undefined ? s.koniec + przesuniecie : null);
  return czasZadaniaMs({ ...zn, koniecMs }, teraz);
}

/**
 * Liczby do wyświetlenia dla stanu `s` w chwili `teraz`:
 *  - `czasOdStartuMs` (do końca zadania, gdy zakończone),
 *  - `odAktualizacjiMs` — od ostatniej ZMIANY etapu; `null`, gdy backend nie zapisał jeszcze żadnego etapu,
 *  - `odSprawdzeniaMs` — od ostatniego udanego odczytu stanu; `null`, gdy jeszcze żadnego nie było,
 *  - `brakAktualizacji` — true, gdy trwa i od ostatniej zmiany etapu (albo od startu, gdy etapów brak) minęło ≥ próg.
 */
export function opis(s, teraz = Date.now()) {
  if (!s) return null;
  const koniec = s.koniec ?? teraz;
  const odKiedy = s.ostatniaAktualizacja ?? s.start;
  const odAktualizacjiMs = s.ostatniaAktualizacja === null ? null : Math.max(0, teraz - s.ostatniaAktualizacja);
  return {
    czasOdStartuMs: czasSerwera(s, teraz) ?? Math.max(0, koniec - s.start),
    odAktualizacjiMs,
    odSprawdzeniaMs: s.ostatnieSprawdzenie === null ? null : Math.max(0, teraz - s.ostatnieSprawdzenie),
    brakAktualizacji: s.status === "trwa" && teraz - odKiedy >= PROG_BRAKU_AKTUALIZACJI_MS,
  };
}
